export const initialCollectionState = Object.freeze({
  loading: false,
  refreshing: false,
  loadingMore: false,
  error: "",
  items: [],
  nextCursor: null,
  metadata: null,
  requestId: 0,
  scopeKey: null,
});

const hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value, key);

export const isFacilitatorAccessError = (error) => ["unauthenticated", "permission-denied"]
  .includes(String(error?.code || "").replace(/^(functions|auth)\//, ""));

function mergeById(current, incoming, key = "id") {
  const values = new Map(current.map((item) => [item[key], item]));
  incoming.forEach((item) => values.set(item[key], item));
  return Array.from(values.values());
}

export function collectionReducer(state, action) {
  switch (action.type) {
    case "request": {
      const hasScopeKey = hasOwn(action, "scopeKey");
      const scopeChanged = hasScopeKey && action.scopeKey !== state.scopeKey;
      const items = scopeChanged ? [] : state.items;
      const append = Boolean(action.append && !scopeChanged);
      return {
        ...state,
        items,
        nextCursor: scopeChanged ? null : state.nextCursor,
        metadata: scopeChanged ? null : state.metadata,
        loading: !items.length && !append,
        refreshing: Boolean(items.length && !append),
        loadingMore: append,
        error: "",
        requestId: action.requestId,
        scopeKey: hasScopeKey ? action.scopeKey : state.scopeKey,
      };
    }
    case "success":
      if (
        action.requestId !== state.requestId
        || (hasOwn(action, "scopeKey") && action.scopeKey !== state.scopeKey)
      ) return state;
      return {
        ...state,
        loading: false,
        refreshing: false,
        loadingMore: false,
        error: "",
        items: action.append
          ? mergeById(state.items, action.items, action.itemKey)
          : action.items,
        nextCursor: action.nextCursor || null,
        metadata: action.metadata || null,
      };
    case "failure":
      if (
        action.requestId !== state.requestId
        || (hasOwn(action, "scopeKey") && action.scopeKey !== state.scopeKey)
      ) return state;
      return {
        ...state,
        ...(action.clearData ? { items: [], nextCursor: null, metadata: null } : {}),
        loading: false,
        refreshing: false,
        loadingMore: false,
        error: action.error,
      };
    case "reset":
      return {
        ...initialCollectionState,
        requestId: state.requestId + 1,
        scopeKey: hasOwn(action, "scopeKey") ? action.scopeKey : null,
      };
    default:
      return state;
  }
}

export function responseMetadata(response = {}) {
  return {
    generatedAt: response.generatedAt || null,
    dataAsOf: response.dataAsOf || null,
    partial: response.partial === true,
    warnings: Array.isArray(response.warnings) ? response.warnings : [],
    capabilities: response.capabilities &&
      typeof response.capabilities === "object" ?
      response.capabilities : {},
    totalAssignedIntakeCount: response.totalAssignedIntakeCount,
    filteredIntakeCount: response.filteredIntakeCount,
    totalAssignedLearnerCount: response.totalAssignedLearnerCount,
    filteredLearnerCount: response.filteredLearnerCount,
  };
}

export function initialResourceState() {
  return {
    loading: true,
    refreshing: false,
    error: "",
    data: null,
    requestId: 0,
    resourceKey: null,
  };
}

export function resourceReducer(state, action) {
  switch (action.type) {
    case "request": {
      const hasResourceKey = hasOwn(action, "resourceKey");
      const resourceChanged = hasResourceKey
        && action.resourceKey !== state.resourceKey;
      const data = resourceChanged ? null : state.data;
      return {
        ...state,
        loading: !data,
        refreshing: Boolean(data),
        error: "",
        data,
        requestId: action.requestId,
        resourceKey: hasResourceKey ? action.resourceKey : state.resourceKey,
      };
    }
    case "success":
      return action.requestId === state.requestId
        && (!hasOwn(action, "resourceKey") || action.resourceKey === state.resourceKey)
        ? {
            ...state,
            loading: false,
            refreshing: false,
            error: "",
            data: action.data,
          }
        : state;
    case "failure":
      return action.requestId === state.requestId
        && (!hasOwn(action, "resourceKey") || action.resourceKey === state.resourceKey)
        ? {
            ...state,
            data: action.clearData ? null : state.data,
            loading: false,
            refreshing: false,
            error: action.error,
          }
        : state;
    default:
      return state;
  }
}
